import { useState, useEffect } from 'react';
import {
  Modal,
  Button,
  Card,
  Tag,
  Alert,
  Spin,
  Space,
  Typography,
  Divider,
  Radio,
  Collapse,
} from 'antd';
import { ReloadOutlined, BulbOutlined, CodeOutlined, FileTextOutlined } from '@ant-design/icons';
import { generateExampleQuestions } from '../../API/services/screeningService';
import { CHAT_ASSISTANT_DEFAULTS } from '../../constants/chatAssistant.constants';
import { t } from '../../i18n/i18n';
import parseAnswerType from '../../utils/filters/answerTypeUtil';
import './ExampleQuestionsModal.scss';

const { Text, Paragraph } = Typography;
const { Panel } = Collapse;

const ExampleQuestionsModal = ({ visible, onClose, skill, evaluationContext }) => {
  const [questions, setQuestions] = useState([]);
  const [loading, setLoading] = useState(false);
  const [cooldownRemaining, setCooldownRemaining] = useState(0);
  const [attemptsUsed, setAttemptsUsed] = useState(0);
  const [error, setError] = useState(null);

  const getDifficultyTranslation = (difficulty) => {
    if (!difficulty) return difficulty;

    const normalizedDifficulty = difficulty.toLowerCase();

    switch (normalizedDifficulty) {
      case 'easy':
        return t('DIFFICULTY_EASY');
      case 'medium':
      case 'intermediate':
        return t('CHAT_DIFFICULTY_INTERMEDIATE');
      case 'hard':
        return t('DIFFICULTY_HARD');
      default:
        return difficulty;
    }
  };

  const getDifficultyColor = (difficulty) => {
    if (!difficulty) return 'default';

    const normalizedDifficulty = difficulty.toLowerCase();

    switch (normalizedDifficulty) {
      case 'easy':
        return 'green';
      case 'medium':
      case 'intermediate':
        return 'orange';
      case 'hard':
        return 'red';
      default:
        return 'default';
    }
  };

  const generateQuestions = async (isRegenerate = false) => {
    setLoading(true);
    setError(null);

    try {
      const questionType = CHAT_ASSISTANT_DEFAULTS.QUESTION_TYPE;
      const language = evaluationContext?.language || 'es';

      const requestData = {
        subskills: [
          {
            id: skill.id,
            name: skill.name,
          },
        ],
        questionType,
        language,
        count: CHAT_ASSISTANT_DEFAULTS.EXAMPLE_QUESTIONS_COUNT,
        context: {
          role:
            evaluationContext?.evaluationMetadata?.name ||
            evaluationContext?.evaluationDraft?.name ||
            'Evaluación técnica',
          difficulty: evaluationContext?.difficultyConfig?.difficulty || 'intermediate',
          purpose: evaluationContext?.purpose || 'RECRUITMENT',
        },
      };

      const response = await generateExampleQuestions(
        requestData.subskills,
        requestData.questionType,
        requestData.language,
        requestData.context
      );

      setQuestions(response);

      if (isRegenerate) {
        setAttemptsUsed(attemptsUsed + 1);
        setCooldownRemaining(CHAT_ASSISTANT_DEFAULTS.COOLDOWN_SECONDS);
      }
    } catch (err) {
      setError(t('CHAT_ERROR_GENERATING_QUESTIONS'));
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    if (cooldownRemaining > 0) {
      const timer = setTimeout(() => {
        setCooldownRemaining(cooldownRemaining - 1);
      }, 1000);
      return () => clearTimeout(timer);
    }
  }, [cooldownRemaining]);

  useEffect(() => {
    if (visible && skill && questions.length === 0) {
      generateQuestions(false);
    }
  }, [visible, skill]);

  const handleClose = () => {
    setQuestions([]);
    setAttemptsUsed(0);
    setCooldownRemaining(0);
    setError(null);
    onClose();
  };

  const renderQuestion = (question, index) => {
    const questionType = question.type || question.answerType || 'TEXT_AI';
    const isCodeQuestion = questionType.includes('CODE');

    return (
      <Panel
        key={index}
        header={
          <Space>
            {isCodeQuestion ? <CodeOutlined /> : <FileTextOutlined />}
            <Text strong>{t('CHAT_QUESTION_NUMBER').replace('{{number}}', index + 1)}</Text>
            <Tag color={isCodeQuestion ? 'blue' : 'green'}>{parseAnswerType(questionType)}</Tag>
            {question.difficulty && (
              <Tag color={getDifficultyColor(question.difficulty)}>
                {getDifficultyTranslation(question.difficulty)}
              </Tag>
            )}
          </Space>
        }
      >
        <Space direction="vertical" style={{ width: '100%' }}>
          <div className="question-content">
            <Text strong>{t('CHAT_EVALUATION_QUESTIONS')}:</Text>
            <Paragraph style={{ marginTop: 8 }}>
              {question.question || question.text || question.content}
            </Paragraph>
          </div>

          {isCodeQuestion && question.codeSnippet && (
            <div className="code-snippet">
              <pre
                style={{
                  background: '#f5f5f5',
                  padding: '12px',
                  borderRadius: '4px',
                  overflow: 'auto',
                }}
              >
                <code>{question.codeSnippet}</code>
              </pre>
            </div>
          )}

          {question.options && question.options.length > 0 && (
            <div className="question-options">
              <Text strong>{t('CHAT_QUESTION_OPTIONS')}:</Text>
              <Radio.Group style={{ marginTop: 8, width: '100%' }}>
                <Space direction="vertical" style={{ width: '100%' }}>
                  {question.options.map((option, optIndex) => (
                    <Radio key={optIndex} value={optIndex} disabled>
                      {option.text || option}
                    </Radio>
                  ))}
                </Space>
              </Radio.Group>
            </div>
          )}

          {(question.correctAnswer !== undefined || question.expectedAnswer) && (
            <div className="correct-answer" style={{ marginTop: 16 }}>
              <Alert
                message={t('CHAT_CORRECT_ANSWER')}
                description={question.correctAnswer || question.expectedAnswer}
                type="info"
                icon={<BulbOutlined />}
              />
            </div>
          )}
          {question.explanation && (
            <div className="explanation" style={{ marginTop: 8 }}>
              <Text type="secondary">
                <strong>{t('CHAT_QUESTION_EXPLANATION')}:</strong> {question.explanation}
              </Text>
            </div>
          )}
        </Space>
      </Panel>
    );
  };

  return (
    <Modal
      title={
        <Space>
          <BulbOutlined />
          <span>{t('CHAT_EXAMPLE_QUESTIONS_FOR').replace('{{skill}}', skill?.name || '')}</span>
        </Space>
      }
      open={visible}
      onCancel={handleClose}
      width={800}
      footer={[
        <Button
          key="regenerate"
          type="primary"
          icon={<ReloadOutlined />}
          onClick={() => generateQuestions(true)}
          disabled={
            loading ||
            cooldownRemaining > 0 ||
            attemptsUsed >= CHAT_ASSISTANT_DEFAULTS.MAX_ATTEMPTS
          }
          loading={loading}
          style={{ borderRadius: 0 }}
        >
          {cooldownRemaining > 0
            ? t('CHAT_WAIT_TO_REGENERATE').replace('{{seconds}}', cooldownRemaining)
            : attemptsUsed >= CHAT_ASSISTANT_DEFAULTS.MAX_ATTEMPTS
            ? t('CHAT_NO_MORE_ATTEMPTS')
            : t('CHAT_GENERATE_MORE_QUESTIONS')}
        </Button>,
        <Button key="close" onClick={handleClose} style={{ borderRadius: 0 }}>
          {t('CHAT_CLOSE_MODAL')}
        </Button>,
      ]}
    >
      <Alert
        message={t('CHAT_EXAMPLE_QUESTIONS_DISCLAIMER')}
        type="warning"
        showIcon
        style={{ marginBottom: 16 }}
      />

      {evaluationContext && (
        <Card size="small" style={{ marginBottom: 16 }}>
          <Space wrap>
            <Text type="secondary">
              <strong>{t('CHAT_QUESTION_ROLE')}:</strong>{' '}
              {evaluationContext.role || evaluationContext.evaluationDraft?.name || 'N/A'}
            </Text>
            <Divider type="vertical" />
            <Text type="secondary">
              <strong>{t('CHAT_QUESTION_DIFFICULTY')}:</strong>{' '}
              {evaluationContext.difficultyConfig?.difficulty ||
                t('CHAT_DIFFICULTY_INTERMEDIATE_LOWERCASE')}
            </Text>
            <Divider type="vertical" />
            <Text type="secondary">
              <strong>{t('CHAT_ATTEMPTS_REMAINING')}:</strong> {CHAT_ASSISTANT_DEFAULTS.MAX_ATTEMPTS - attemptsUsed}/
              {CHAT_ASSISTANT_DEFAULTS.MAX_ATTEMPTS}
            </Text>
          </Space>
        </Card>
      )}

      {loading && questions.length === 0 && (
        <div style={{ textAlign: 'center', padding: '40px' }}>
          <Spin size="large" />
          <div style={{ marginTop: 16 }}>
            <Text>{t('CHAT_GENERATING_QUESTIONS')}</Text>
          </div>
        </div>
      )}

      {error && <Alert message={error} type="error" showIcon style={{ marginBottom: 16 }} />}

      {!loading && questions.length > 0 && (
        <Collapse accordion>
          {questions.map((question, index) => renderQuestion(question, index))}
        </Collapse>
      )}

      {loading && questions.length > 0 && (
        <div
          style={{
            position: 'absolute',
            top: 0,
            left: 0,
            right: 0,
            bottom: 0,
            background: 'rgba(255,255,255,0.8)',
            display: 'flex',
            alignItems: 'center',
            justifyContent: 'center',
            zIndex: 1000,
          }}
        >
          <Spin size="large" />
        </div>
      )}
    </Modal>
  );
};

export default ExampleQuestionsModal;
